import React, { useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import PageConfig from '@/component/layout/PageConfig.tsx'
import { TYPE_TopNav } from '@/component/layout/TopNav/TopNav.tsx'
import Footer from '@/component/layout/Footer/Footer.tsx'
import SalesModal from '@/pages/landing/Home/SalesModal.tsx'
import { useAuth } from '@/hooks/useAuth'
import { getHomeCopy } from '@/pages/landing/Home/homeContent.ts'
import IntroductionImages from '@/assets/bg_home_introductions_images2.png'
import CarearlyInUse from '@/assets/home_carearly_in_use.jpg'
import CarearlyVest from '@/assets/home_carearly_vest.jpg'
import CarearlyLifelog from '@/assets/home_carearly_lifelog.jpg'
import CarearlyReport from '@/assets/home_carearly_report.jpg'
import CareNetwork from '@/assets/home_care_network_v3.jpg'
import FeatureAlert from '@/assets/img_home_feature_carousel2_1.jpg'
import FeatureLocation from '@/assets/img_home_feature_carousel3_2.jpg'
import FeatureManage from '@/assets/img_home_feature_carousel4_2.jpg'
import Case1Img from '@/assets/img_home_case_1.jpg'
import Case2Img from '@/assets/img_home_case_2.jpg'
import Cooperation1 from '@/assets/home_case_cooperation1.png'
import Cooperation2 from '@/assets/home_case_cooperation2.png'
import Cooperation3 from '@/assets/home_case_cooperation3.png'
import Cooperation4 from '@/assets/home_case_cooperation4.png'
import Cooperation5 from '@/assets/home_case_cooperation5.png'
import Cooperation6 from '@/assets/home_case_cooperation6.png'
import Cooperation7 from '@/assets/home_case_cooperation7.png'
import CooperationBwave from '@/assets/home_case_cooperation_bwave.png'
import CooperationInhand from '@/assets/home_case_cooperation_inhand.png'
import IoTSecurityCertification from '@/assets/badge/carearly_iot_security_certified_basic.png'
import BioIcon from '@/assets/ic_home_major_btn_bio.svg?react'
import AlertIcon from '@/assets/ic_home_major_btn_alert.svg?react'
import LocationIcon from '@/assets/ic_home_major_btn_location.svg?react'
import ManageIcon from '@/assets/ic_home_major_btn_manage.svg?react'
import './Home.css'

const IOT_SECURITY_CERTIFICATION_URL = 'https://www.ksecurity.or.kr/user/extra/kisis/310/iot/iotView/jsp/LayOutPage.do?setIdx=&dataIdx=604&iotField=&column=&search='

export interface IProps {
    className?: string
}

interface SalesModalHandle {
    show: () => void
}

const Home: React.FC<IProps> = () => {
    const { t, i18n } = useTranslation()
    const copy = useMemo(
        () => getHomeCopy(i18n.resolvedLanguage || i18n.language),
        [i18n.resolvedLanguage, i18n.language]
    )
    const salesModalRef = useRef<SalesModalHandle | null>(null)
    const navigate = useNavigate()
    const { isLoggedIn } = useAuth()

    const openSalesModal = () => salesModalRef.current?.show()
    const goToService = () => navigate(isLoggedIn ? '/dashboard' : '/login')

    const features = [
        {
            id: 'lifelog',
            title: t('home.feature.options.bio.title'),
            description: copy.featureDescriptions[0],
            image: CarearlyLifelog,
            icon: <BioIcon />,
            className: 'home-feature-card--wide home-feature-card--blue'
        },
        {
            id: 'alert',
            title: t('home.feature.options.emergency.title'),
            description: copy.featureDescriptions[1],
            image: FeatureAlert,
            icon: <AlertIcon />,
            className: 'home-feature-card--red'
        },
        {
            id: 'location',
            title: t('home.feature.options.location.title'),
            description: copy.featureDescriptions[2],
            image: FeatureLocation,
            icon: <LocationIcon />,
            className: 'home-feature-card--cyan'
        },
        {
            id: 'management',
            title: t('home.feature.options.users.title'),
            description: copy.featureDescriptions[3],
            image: FeatureManage,
            icon: <ManageIcon />,
            className: 'home-feature-card--wide home-feature-card--navy'
        },
        {
            id: 'report',
            title: t('sideNav.menu.HealthCare'),
            description: copy.featureDescriptions[4],
            image: CarearlyReport,
            icon: <BioIcon />,
            className: 'home-feature-card--full home-feature-card--violet'
        }
    ]

    const cases = [
        {
            title: t('home.introductionCase.case1.title'),
            description: t('home.introductionCase.case1.desc'),
            image: Case1Img,
            href: 'https://n.news.naver.com/article/008/0005049814'
        },
        {
            title: t('home.introductionCase.case2.title'),
            description: t('home.introductionCase.case2.desc'),
            image: Case2Img,
            href: 'https://www.sportsseoul.com/news/read/1440417?ref=naver'
        }
    ]

    const partnerLogos = [
        Cooperation1,
        Cooperation2,
        Cooperation3,
        Cooperation4,
        Cooperation5,
        CooperationBwave,
        CooperationInhand,
        Cooperation6,
        Cooperation7
    ]

    return (
        <>
            <PageConfig showTopNav={true} typeTopNav={TYPE_TopNav.GUIDE}>
                <div className="home-v2">
                    <section className="home-hero" aria-labelledby="home-hero-title">
                        <div className="home-shell home-hero__grid">
                            <div className="home-hero__copy">
                                <p className="home-eyebrow">
                                    <span className="home-eyebrow__dot" />
                                    {copy.heroEyebrow}
                                </p>
                                <h1 id="home-hero-title" className="home-hero__title">
                                    {copy.heroTitle}
                                    <span>{copy.heroAccent}</span>
                                </h1>
                                <p className="home-hero__description">{copy.heroDescription}</p>
                                <div className="home-actions">
                                    <button className="home-button home-button--primary" onClick={goToService}>
                                        {copy.heroPrimary}
                                        <span aria-hidden="true">→</span>
                                    </button>
                                    <button className="home-button home-button--secondary" onClick={openSalesModal}>
                                        {copy.heroSecondary}
                                    </button>
                                </div>
                                <p className="home-hero__note">{copy.heroNote}</p>
                            </div>

                            <div className="home-hero__visual" aria-label={copy.heroVisualLabel}>
                                <div className="home-hero__orb home-hero__orb--one" />
                                <div className="home-hero__orb home-hero__orb--two" />
                                <div className="home-hero__visual-frame">
                                    <div className="home-hero__visual-topline">
                                        <span>CAREARLY CARE CONSOLE</span>
                                        <span className="home-live-badge"><i /> LIVE</span>
                                    </div>
                                    <img src={IntroductionImages} alt={copy.heroImageAlt} />
                                </div>
                                <div className="home-floating-card home-floating-card--alert">
                                    <span className="home-floating-card__icon">!</span>
                                    <span>
                                        <strong>{t('home.feature.options.emergency.title')}</strong>
                                        <small>Safety event detected</small>
                                    </span>
                                </div>
                                <div className="home-floating-card home-floating-card--signal">
                                    <span className="home-signal-line" aria-hidden="true">⌁⌁⌁</span>
                                    <span>
                                        <strong>Live lifelog</strong>
                                        <small>Heart · Respiration · GPS</small>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section className="home-metrics" aria-label={copy.metricsLabel}>
                        <div className="home-shell">
                            <div className="home-metrics__grid">
                                {copy.metrics.map(metric => (
                                    <div className="home-metric" key={metric.label}>
                                        <strong>{metric.value}</strong>
                                        <span>{metric.label}</span>
                                    </div>
                                ))}
                            </div>
                            <p className="home-metrics__note">* {copy.metricsNote}</p>
                        </div>
                    </section>

                    <section className="home-section home-challenge" aria-labelledby="home-challenge-title">
                        <div className="home-shell">
                            <div className="home-section-heading home-section-heading--center">
                                <p className="home-eyebrow">{copy.challengeEyebrow}</p>
                                <h2 id="home-challenge-title">{copy.challengeTitle}</h2>
                                <p>{copy.challengeDescription}</p>
                            </div>
                            <div className="home-challenge__grid">
                                {copy.challenges.map(challenge => (
                                    <article className="home-challenge-card" key={challenge.number}>
                                        <span>{challenge.number}</span>
                                        <h3>{challenge.title}</h3>
                                        <p>{challenge.description}</p>
                                    </article>
                                ))}
                            </div>
                            <div className="home-solution-ribbon">
                                <span className="home-solution-ribbon__mark">C</span>
                                <p>{copy.solutionQuote}</p>
                            </div>
                        </div>
                    </section>

                    <section className="home-section home-technology" aria-labelledby="home-technology-title">
                        <div className="home-shell home-technology__grid">
                            <div className="home-technology__product">
                                <div className="home-technology__photo">
                                    <img src={CarearlyVest} alt={copy.vestImageAlt} />
                                </div>
                                <div className="home-technology__wearing">
                                    <img src={CarearlyInUse} alt={copy.wearingImageAlt} />
                                    <span>{copy.productCaption}</span>
                                </div>
                            </div>
                            <div className="home-technology__copy">
                                <p className="home-eyebrow home-eyebrow--light">{copy.technologyEyebrow}</p>
                                <h2 id="home-technology-title">{copy.technologyTitle}</h2>
                                <p className="home-technology__description">{copy.technologyDescription}</p>
                                <ul className="home-check-list">
                                    {copy.technologyPoints.map(point => (
                                        <li key={point}><span>✓</span>{point}</li>
                                    ))}
                                </ul>
                                <div className="home-product-specs">
                                    {copy.productSpecs.map(spec => (
                                        <div key={spec.label}>
                                            <strong>{spec.value}</strong>
                                            <span>{spec.label}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </section>

                    <section id="features" className="home-section home-features" aria-labelledby="home-features-title">
                        <div className="home-shell">
                            <div className="home-section-heading">
                                <p className="home-eyebrow">{copy.featuresEyebrow}</p>
                                <h2 id="home-features-title">{copy.featuresTitle}</h2>
                                <p>{copy.featuresDescription}</p>
                            </div>
                            <div className="home-feature-grid">
                                {features.map(feature => (
                                    <article className={`home-feature-card ${feature.className}`} key={feature.id}>
                                        <div className="home-feature-card__copy">
                                            <div className="home-feature-card__icon">{feature.icon}</div>
                                            <h3>{feature.title}</h3>
                                            <p>{feature.description}</p>
                                        </div>
                                        <div className="home-feature-card__screen">
                                            <img src={feature.image} alt={copy.featureImageAlt(feature.title)} loading="lazy" />
                                        </div>
                                    </article>
                                ))}
                            </div>
                        </div>
                    </section>

                    <section className="home-section home-operations" aria-labelledby="home-operations-title">
                        <div className="home-shell home-operations__layout">
                            <div className="home-operations__visual">
                                <img src={CareNetwork} alt={copy.networkImageAlt} loading="lazy" />
                            </div>
                            <div className="home-operations__copy">
                                <p className="home-eyebrow">{copy.operationsEyebrow}</p>
                                <h2 id="home-operations-title">{copy.operationsTitle}</h2>
                                <p className="home-operations__description">{copy.operationsDescription}</p>
                                <ol className="home-operation-list">
                                    {copy.operationSteps.map(item => (
                                        <li key={item.step}>
                                            <span>{item.step}</span>
                                            <div>
                                                <h3>{item.title}</h3>
                                                <p>{item.description}</p>
                                            </div>
                                        </li>
                                    ))}
                                </ol>
                            </div>
                        </div>
                    </section>

                    <section className="home-section home-evidence" aria-labelledby="home-evidence-title">
                        <div className="home-shell">
                            <div className="home-section-heading home-section-heading--center home-section-heading--light">
                                <p className="home-eyebrow home-eyebrow--light">{copy.evidenceEyebrow}</p>
                                <h2 id="home-evidence-title">{copy.evidenceTitle}</h2>
                                <p>{copy.evidenceDescription}</p>
                            </div>
                            <div className="home-evidence__grid">
                                <a
                                    className="home-security-certification"
                                    href={IOT_SECURITY_CERTIFICATION_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={`${copy.securityCertification.title} - ${copy.securityCertification.guide}`}
                                >
                                    <div className="home-security-certification__mark">
                                        <img
                                            src={IoTSecurityCertification}
                                            alt={copy.securityCertification.imageAlt}
                                            loading="lazy"
                                        />
                                    </div>
                                    <div className="home-security-certification__copy">
                                        <span>{copy.securityCertification.eyebrow}</span>
                                        <h3>{copy.securityCertification.title}</h3>
                                        <p>{copy.securityCertification.description}</p>
                                        <small>{copy.securityCertification.guide}<b aria-hidden="true">↗</b></small>
                                    </div>
                                </a>
                                <article className="home-gs-certification">
                                    <span className="home-gs-certification__eyebrow">{copy.gsCertification.eyebrow}</span>
                                    <strong>GS</strong>
                                    <h3>{copy.gsCertification.title}</h3>
                                    <p>{copy.gsCertification.description}</p>
                                    <small className="home-gs-certification__status">{copy.gsCertification.status}</small>
                                </article>
                                {copy.evidenceItems.map(item => (
                                    <article
                                        key={item.label}
                                        className={item.variant === 'pending' ? 'home-evidence-card--pending' : undefined}
                                    >
                                        {item.eyebrow && <span className="home-evidence-card__eyebrow">{item.eyebrow}</span>}
                                        <strong>{item.value}</strong>
                                        <h3>{item.label}</h3>
                                        <p>{item.description}</p>
                                        {item.status && <small className="home-evidence-card__status">{item.status}</small>}
                                        {item.links && (
                                            <div className="home-evidence__links">
                                                {item.links.map(link => (
                                                    <a
                                                        key={link.href}
                                                        href={link.href}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {link.label}<span aria-hidden="true">↗</span>
                                                    </a>
                                                ))}
                                            </div>
                                        )}
                                    </article>
                                ))}
                            </div>
                        </div>
                    </section>

                    <section className="home-section home-cases" aria-labelledby="home-case-title">
                        <div className="home-shell">
                            <div className="home-section-heading home-section-heading--center">
                                <p className="home-eyebrow">FIELD STORIES</p>
                                <h2 id="home-case-title">{copy.caseTitle}</h2>
                            </div>
                            <div className="home-cases__grid">
                                {cases.map(item => (
                                    <a
                                        key={item.href}
                                        href={item.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="home-case-card"
                                    >
                                        <img src={item.image} alt="" loading="lazy" />
                                        <div>
                                            <span>PUBLIC CARE</span>
                                            <h3>{item.title}</h3>
                                            <p>{item.description}</p>
                                            <strong>{copy.caseMore} →</strong>
                                        </div>
                                    </a>
                                ))}
                            </div>
                            <div className="home-partners">
                                <p>{t('home.introductionCase.cooperate.title')}</p>
                                <div>
                                    {partnerLogos.map((logo, index) => (
                                        <img key={`${logo}-${index}`} src={logo} alt="" loading="lazy" />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </section>

                    <section className="home-cta" aria-labelledby="home-cta-title">
                        <div className="home-shell">
                            <div className="home-cta__panel">
                                <div>
                                    <p className="home-eyebrow home-eyebrow--light">{copy.ctaEyebrow}</p>
                                    <h2 id="home-cta-title">{copy.ctaTitle}</h2>
                                    <p>{copy.ctaDescription}</p>
                                </div>
                                <div className="home-actions">
                                    <button className="home-button home-button--white" onClick={openSalesModal}>
                                        {copy.ctaPrimary}
                                    </button>
                                    <button className="home-button home-button--ghost" onClick={goToService}>
                                        {copy.ctaSecondary}
                                        <span aria-hidden="true">→</span>
                                    </button>
                                </div>
                            </div>
                            <p className="home-disclaimer">{copy.disclaimer}</p>
                        </div>
                    </section>
                    <Footer />
                </div>
            </PageConfig>
            <SalesModal ref={salesModalRef} />
        </>
    )
}

export default Home
